import {
  action,
  atom,
  reatomAsync,
  sleep,
  withConcurrency,
  withAbort,
} from '@reatom/framework'
import i18next from 'i18next'

import { AsyncStateN } from '@blade/std-lib'
import { reatomUpdatePremiumCurrency } from '@root/features/premium/update-currency'
import { reatomPreviewEmoji } from '@root/features/services/preview-emoji'
import {
  DEMO_MODE,
  DomUtil,
  environmentAtom,
  Error,
  StringUtil,
} from '@root/shared'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'
import type { PremiumMetaModel } from '@root/entities/premium'

const POLL_INTERVAL = 5

export const EMOJI_IMAGE = {
  dimensions: { width: 256, height: 256 },
}

export const reatomUpdateEmojiDrawer = ({
  premiumMeta,
  currencyAddress,
}: {
  currencyAddress: CurrencyAddress
  premiumMeta: PremiumMetaModel
}) => {
  const { updateCurrencyAction, updateRequestState, resetStateAction } =
    reatomUpdatePremiumCurrency()

  const { loadPreviewLink, previewRequestStateAtom, resetPreviewStateAction } =
    reatomPreviewEmoji({
      premiumMeta: premiumMeta,
    })

  const pollingStateAtom = atom(
    AsyncStateN.notInitiated<Error.UnknownError, undefined>(),
    'pollingStateAtom',
  )

  const imageAtom = atom<File | undefined>(undefined, 'imageAtom')

  const setImageAction = action(async (ctx, image: File) => {
    resetPreviewStateAction(ctx)
    pollingStateAtom(ctx, AsyncStateN.notInitiated())
    startEmojiPollingReaction.abort(ctx, 'new request')

    const img = await ctx.schedule(() => DomUtil.fileToImage(image))
    if (!img) {
      return false
    }
    const meetsDimensions =
      DEMO_MODE ||
      (img.width <= EMOJI_IMAGE.dimensions.width &&
        img.height <= EMOJI_IMAGE.dimensions.height)

    if (!meetsDimensions) {
      showSnackbarAction(ctx, {
        text: StringUtil.capitalize(
          i18next.t('premium.image-upload.size', {
            size: `${EMOJI_IMAGE.dimensions.width}x${EMOJI_IMAGE.dimensions.height}`,
          }),
        ),
        intent: 'error',
      })
      return
    }

    loadPreviewLink(ctx, { emoji: image })
  }, 'setImageAction')

  const updateEmojiInternalAction = action((ctx) => {
    const state = ctx.get(previewRequestStateAtom)
    const premiumId = ctx.get(premiumMeta.premiumId)
    if (!AsyncStateN.isSuccess(state) || !premiumId) {
      return
    }
    updateCurrencyAction(ctx, {
      emojiId: state.value.customEmojiId,
      premiumId,
      currencyAddress: currencyAddress,
    })
  }, 'updateEmojiInternalAction')

  const resetAction = action((ctx) => {
    imageAtom(ctx, undefined)
    resetPreviewStateAction(ctx)
    resetStateAction(ctx)
    startEmojiPollingReaction.abort(ctx, 'dispose')
  }, 'resetAction')

  const startEmojiPollingReaction = reatomAsync(async (ctx) => {
    const emojiId = ctx.get(previewRequestStateAtom).value?.customEmojiId
    if (!emojiId) {
      return undefined
    }

    pollingStateAtom(ctx, AsyncStateN.loading())

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
      const emojiId = ctx.get(previewRequestStateAtom).value?.customEmojiId
      if (!emojiId) {
        return undefined
      }

      const host = ctx.get(environmentAtom).assets.customEmojiHost
      const status = await ctx.schedule(() =>
        checkAssetStatus(`${host}/${emojiId}.webm`),
      )
      if (status == 200) {
        pollingStateAtom(ctx, AsyncStateN.success(undefined))
        return
      }
      if (status !== 404) {
        pollingStateAtom(
          ctx,
          AsyncStateN.error(Error.UnknownError.fromUnknown(status)),
        )
        return
      }
      await ctx.schedule(() => sleep(POLL_INTERVAL * 1000))
    }
  }, 'startEmojiPollingReaction').pipe(withConcurrency(), withAbort())

  previewRequestStateAtom.onChange((ctx) => {
    startEmojiPollingReaction(ctx)
  })

  const disposeAction = action((ctx) => {
    startEmojiPollingReaction.abort(ctx, 'dispose')
  }, 'disposeAction')

  updateRequestState.onChange((ctx, state) => {
    if (AsyncStateN.isError(state)) {
      showSnackbarAction(ctx, {
        text: i18next.t('common.something_went_wrong_try_again'),
        intent: 'error',
      })
    }
  })

  return {
    updateRequestState,
    updateCurrencyAction: updateEmojiInternalAction,
    previewRequestStateAtom,
    pollingStateAtom,
    setImageAction,
    resetAction,
    disposeAction,
  }
}

const checkAssetStatus = async (url: string) => {
  // All necessary to avoid caching
  const uniqUrl = `${url}?rnd=${Date.now()}`
  try {
    const result = await fetch(uniqUrl, {
      cache: 'reload',
      headers: {
        'Cache-Control': 'no-store',
        Pragma: 'no-cache',
      },
    })
    return result.status
  } catch {
    return undefined
  }
}
